import { z } from "zod";

/**
 * Statuses of a test result within a test run.
 * Aligns with spec: Passed, Failed, Blocked, Untested, Flaky, Healed, Needs review.
 */
export const testResultStatuses = [
  "Passed",
  "Failed",
  "Blocked",
  "Untested",
  "Flaky",
  "Healed",
  "Needs review",
] as const;
export const testResultStatusSchema = z.enum(testResultStatuses);
export type TestResultStatus = z.infer<typeof testResultStatusSchema>;

/**
 * Status of a test run overall.
 */
export const testRunStatuses = ["Passed", "Failed", "Needs review", "Running", "Blocked"] as const;
export const testRunStatusSchema = z.enum(testRunStatuses);
export type TestRunStatus = z.infer<typeof testRunStatusSchema>;

/**
 * Type of test case: manual, automated by agent on web, or API test by agent.
 */
export const testCaseTypes = ["manual", "agent-web", "agent-api"] as const;
export const testCaseTypeSchema = z.enum(testCaseTypes);
export type TestCaseType = z.infer<typeof testCaseTypeSchema>;

/**
 * Priority of a test case.
 */
export const testCasePriorities = ["P0", "P1", "P2", "P3"] as const;
export const testCasePrioritySchema = z.enum(testCasePriorities);
export type TestCasePriority = z.infer<typeof testCasePrioritySchema>;

/**
 * Triage verdict assigned by an agent or human.
 */
export const testTriageVerdicts = ["product_bug", "test_issue", "environment", "flaky"] as const;
export const testTriageVerdictSchema = z.enum(testTriageVerdicts);
export type TestTriageVerdict = z.infer<typeof testTriageVerdictSchema>;

/**
 * Step definition in a test case.
 */
export const testStepSchema = z.object({
  order: z.number().int().min(1),
  action: z.string().min(1),
  expected: z.string().optional(),
});
export type TestStep = z.infer<typeof testStepSchema>;

/**
 * Test Suite schema.
 */
export const testSuiteSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  name: z.string().min(1),
  description: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type TestSuite = z.infer<typeof testSuiteSchema>;

export const createTestSuiteSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(1000).optional(),
});
export type CreateTestSuiteInput = z.infer<typeof createTestSuiteSchema>;

/**
 * Test Section schema.
 */
export const testSectionSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  suiteId: z.uuid(),
  parentId: z.uuid().nullable(),
  name: z.string().min(1),
  description: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type TestSection = z.infer<typeof testSectionSchema>;

export const createTestSectionSchema = z.object({
  suiteId: z.uuid(),
  parentId: z.uuid().optional(),
  name: z.string().min(1).max(120),
  description: z.string().max(1000).optional(),
});
export type CreateTestSectionInput = z.infer<typeof createTestSectionSchema>;

/**
 * Test Case schema.
 */
export const testCaseSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  suiteId: z.uuid(),
  sectionId: z.uuid().nullable(),
  key: z.string().min(1),
  title: z.string().min(1),
  type: testCaseTypeSchema,
  priority: testCasePrioritySchema,
  steps: z.array(testStepSchema),
  expectedResult: z.string().nullable(),
  scriptPath: z.string().nullable(),
  tags: z.array(z.string()),
  requirementWorkItemIds: z.array(z.uuid()).optional(),
  // QA6: author and approval fields. Only approved cases may gate.
  createdBySubject: z.string().nullable(),
  approved: z.boolean(),
  approvedBy: z.string().nullable(),
  approvedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type TestCase = z.infer<typeof testCaseSchema>;

export const createTestCaseSchema = z.object({
  suiteId: z.uuid(),
  sectionId: z.uuid().optional(),
  key: z.string().min(1).max(50),
  title: z.string().min(1).max(255),
  type: testCaseTypeSchema.default("manual"),
  priority: testCasePrioritySchema.default("P1"),
  steps: z.array(testStepSchema).default([]),
  expectedResult: z.string().optional(),
  scriptPath: z.string().optional(),
  tags: z.array(z.string()).default([]),
  requirementWorkItemIds: z.array(z.uuid()).optional(),
  createdBySubject: z.string().optional(),
});
export type CreateTestCaseInput = z.infer<typeof createTestCaseSchema>;

export const updateTestCaseSchema = z.object({
  title: z.string().min(1).max(255).optional(),
  key: z.string().min(1).max(50).optional(),
  type: testCaseTypeSchema.optional(),
  priority: testCasePrioritySchema.optional(),
  steps: z.array(testStepSchema).optional(),
  expectedResult: z.string().optional().nullable(),
  scriptPath: z.string().optional().nullable(),
  tags: z.array(z.string()).optional(),
  requirementWorkItemIds: z.array(z.uuid()).optional(),
});
export type UpdateTestCaseInput = z.infer<typeof updateTestCaseSchema>;

/**
 * Case approval request: a reviewer (not the author) approves or revokes
 * a case for gating.
 */
export const approveTestCaseSchema = z.object({
  approved: z.boolean(),
});
export type ApproveTestCaseInput = z.infer<typeof approveTestCaseSchema>;

/**
 * Test Plan schema.
 */
export const testPlanSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  name: z.string().min(1),
  environment: z.string().min(1),
  triggerRule: z.string().min(1),
  description: z.string().nullable(),
  caseCount: z.number().int().min(0).optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type TestPlan = z.infer<typeof testPlanSchema>;

export const createTestPlanSchema = z.object({
  name: z.string().min(1).max(120),
  environment: z.string().min(1).max(80),
  triggerRule: z.string().min(1).max(200),
  description: z.string().max(1000).optional(),
  testCaseIds: z.array(z.uuid()).default([]),
});
export type CreateTestPlanInput = z.infer<typeof createTestPlanSchema>;

/**
 * Test Result schema.
 */
export const testResultSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  runId: z.uuid(),
  testCaseId: z.uuid(),
  testCaseKey: z.string().optional(),
  testCaseTitle: z.string().optional(),
  status: testResultStatusSchema,
  attempt: z.number().int().min(1),
  durationMs: z.number().int().min(0),
  failureMessage: z.string().nullable(),
  triageVerdict: testTriageVerdictSchema.nullable(),
  evidenceDigest: z.string().nullable(),
  logs: z.string().nullable(),
  createdAt: z.iso.datetime(),
});
export type TestResult = z.infer<typeof testResultSchema>;

export const recordTestResultSchema = z.object({
  testCaseId: z.uuid(),
  status: testResultStatusSchema,
  attempt: z.number().int().min(1).default(1),
  durationMs: z.number().int().min(0).default(0),
  failureMessage: z.string().optional(),
  triageVerdict: testTriageVerdictSchema.optional(),
  evidenceDigest: z.string().optional(),
  logs: z.string().optional(),
  defectWorkItemId: z.uuid().optional(),
});
export type RecordTestResultInput = z.infer<typeof recordTestResultSchema>;

/**
 * Test Run schema.
 */
export const testRunCountsSchema = z.object({
  passed: z.number().int().min(0),
  failed: z.number().int().min(0),
  blocked: z.number().int().min(0),
  untested: z.number().int().min(0),
  flaky: z.number().int().min(0),
  healed: z.number().int().min(0),
  needsReview: z.number().int().min(0),
  total: z.number().int().min(0),
});
export type TestRunCounts = z.infer<typeof testRunCountsSchema>;

export const testRunSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  planId: z.uuid().nullable(),
  name: z.string().min(1),
  environment: z.string().min(1),
  trigger: z.string().min(1),
  status: testRunStatusSchema,
  durationMs: z.number().int().min(0),
  counts: testRunCountsSchema,
  results: z.array(testResultSchema).optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type TestRun = z.infer<typeof testRunSchema>;

export const createTestRunSchema = z.object({
  planId: z.uuid().optional(),
  name: z.string().min(1).max(120),
  environment: z.string().min(1).max(80),
  trigger: z.string().min(1).max(200),
  status: testRunStatusSchema.default("Running"),
  durationMs: z.number().int().min(0).default(0),
  results: z.array(recordTestResultSchema).optional(),
});
export type CreateTestRunInput = z.infer<typeof createTestRunSchema>;

/**
 * Traceability: mapping from a Work Item to its proving test cases and results.
 */
export const workItemTraceabilitySchema = z.object({
  workItemId: z.uuid(),
  workItemKey: z.string(),
  workItemTitle: z.string(),
  testCount: z.number().int().min(0),
  passedCount: z.number().int().min(0),
  failedCount: z.number().int().min(0),
  blockedCount: z.number().int().min(0),
  coverageStatus: z.enum(["covered", "failing", "blocked", "untested", "no_tests"]),
  testCases: z.array(
    z.object({
      id: z.uuid(),
      key: z.string(),
      title: z.string(),
      lastResultStatus: testResultStatusSchema.nullable(),
    }),
  ),
});
export type WorkItemTraceability = z.infer<typeof workItemTraceabilitySchema>;

/**
 * Kinds of environment a test plan may run against. Production is off by default and only ever
 * serves read-only checks, and only behind an approval.
 */
export const testEnvironmentKinds = ["local", "staging", "preview", "production"] as const;
export const testEnvironmentKindSchema = z.enum(testEnvironmentKinds);
export type TestEnvironmentKind = z.infer<typeof testEnvironmentKindSchema>;

/**
 * What a check does against the environment. A production environment only ever serves reads.
 */
export const testEnvironmentCheckModes = ["read", "write"] as const;
export const testEnvironmentCheckModeSchema = z.enum(testEnvironmentCheckModes);
export type TestEnvironmentCheckMode = z.infer<typeof testEnvironmentCheckModeSchema>;

/**
 * A reference to a credential held in Integrations. The shape is deliberately narrow: a
 * reference names the credential and never carries its value, so an environment record can be
 * listed, logged, and handed to a sandbox as it is.
 */
export const credentialReferenceSchema = z
  .string()
  .trim()
  .min(1)
  .max(120)
  .regex(
    /^[A-Za-z0-9][A-Za-z0-9._:/-]*$/,
    "A credential reference names the credential; it never holds a value.",
  );
export type CredentialReference = z.infer<typeof credentialReferenceSchema>;

/**
 * One test environment: the address a run tests and the references to the test accounts it may
 * use. The credential values stay in Integrations and never reach the sandbox or the logs.
 */
export const testEnvironmentSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  name: z.string().min(1),
  kind: testEnvironmentKindSchema,
  baseUrl: z.string().min(1),
  readOnly: z.boolean(),
  enabled: z.boolean(),
  pullRequestNumber: z.number().int().positive().nullable(),
  credentialRefs: z.array(credentialReferenceSchema),
  // The subject that created the environment. The author never approves their own.
  createdBySubject: z.string().nullable(),
  approvedBy: z.string().nullable(),
  approvedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type TestEnvironment = z.infer<typeof testEnvironmentSchema>;

export const createTestEnvironmentSchema = z.object({
  name: z.string().trim().min(1).max(120),
  kind: testEnvironmentKindSchema,
  baseUrl: z.string().trim().min(1).max(2048),
  credentialRefs: z.array(credentialReferenceSchema).max(50).default([]),
  pullRequestNumber: z.number().int().positive().optional(),
  readOnly: z.boolean().default(false),
});
export type CreateTestEnvironmentInput = z.infer<typeof createTestEnvironmentSchema>;

export const updateTestEnvironmentSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  baseUrl: z.string().trim().min(1).max(2048).optional(),
  credentialRefs: z.array(credentialReferenceSchema).max(50).optional(),
  pullRequestNumber: z.number().int().positive().nullable().optional(),
  readOnly: z.boolean().optional(),
  enabled: z.boolean().optional(),
});
export type UpdateTestEnvironmentInput = z.infer<typeof updateTestEnvironmentSchema>;

/**
 * `POST /api/v1/projects/:id/quality/environments/:environmentId/approval`. Approving lets
 * read-only checks reach a production environment; withdrawing the approval closes it again.
 */
export const approveTestEnvironmentSchema = z.object({
  approved: z.boolean(),
});
export type ApproveTestEnvironmentInput = z.infer<typeof approveTestEnvironmentSchema>;

/**
 * What the control plane hands to a sandbox for one environment: the address to test and the
 * references to the test accounts. The credential values are not here, so nothing that reaches
 * the sandbox or its logs can carry one.
 */
/**
 * `POST /api/v1/projects/:id/quality/environments/:environmentId/payload`. The body says what
 * the check does, so the server can decide whether the environment may serve it.
 */
export const resolveTestEnvironmentSchema = z.object({
  mode: testEnvironmentCheckModeSchema,
  checkName: z.string().trim().min(1).max(120),
});
export type ResolveTestEnvironmentInput = z.infer<typeof resolveTestEnvironmentSchema>;

export const testEnvironmentPayloadSchema = z.object({
  environmentId: z.uuid(),
  name: z.string().min(1),
  kind: testEnvironmentKindSchema,
  baseUrl: z.string().min(1),
  readOnly: z.boolean(),
  credentialRefs: z.array(credentialReferenceSchema),
});
export type TestEnvironmentPayload = z.infer<typeof testEnvironmentPayloadSchema>;

/**
 * Quality overview KPIs.
 */
export const qualityOverviewSchema = z.object({
  passRatePercent: z.number().min(0).max(100),
  automatedPercent: z.number().min(0).max(100),
  flakyCount: z.number().int().min(0),
  openDefectsCount: z.number().int().min(0),
  totalTests: z.number().int().min(0),
  automatedTests: z.number().int().min(0),
  releaseGateThreshold: z.number().min(0).max(100),
  releaseGateMet: z.boolean(),
});
export type QualityOverview = z.infer<typeof qualityOverviewSchema>;

/**
 * Quality and cost metrics (QA20). The agent runtime derives them from its
 * test and eval tables, and the console only reads them. A figure the runtime
 * cannot measure is null, never zero, so an unmeasured value cannot read as a
 * good one.
 */
const metricPercentSchema = z.number().int().min(0).max(100);
const metricCountSchema = z.number().int().min(0);

export const qualityMetricsSchema = z.object({
  requirements: z.object({
    total: metricCountSchema,
    withPassingApprovedTest: metricCountSchema,
    sharePercent: metricPercentSchema.nullable(),
  }),
  flake: z.object({
    executedResults: metricCountSchema,
    flakyResults: metricCountSchema,
    ratePercent: metricPercentSchema.nullable(),
  }),
  heals: z.object({
    healed: metricCountSchema,
    reversedByPerson: metricCountSchema.nullable(),
  }),
  triage: z.object({
    secondsToVerdict: metricCountSchema.nullable(),
  }),
  cost: z.object({
    acceptedTests: metricCountSchema,
    evalSpendMicrousd: metricCountSchema,
    perAcceptedTestMicrousd: metricCountSchema.nullable(),
  }),
});
export type QualityMetrics = z.infer<typeof qualityMetricsSchema>;
